import { useQuery } from "@tanstack/react-query";
import {
  announcementsApi,
  blogsApi,
  eventsApi,
  communitiesApi,
  learningApi,
  careerApi,
  reportsApi,
  discussionsApi,
  organizationsApi,
  qaApi,
  mentorshipApi,
  complianceApi,
} from "@/lib/api/services";
import type {
  HomepageArticle,
  HomepageEvent,
  HomepageCourse,
  HomepageCommunity,
  HomepageJob,
  HomepageReport,
  HomepageDiscussion,
  HomepageOrganization,
  HomepageQA,
  HomepageMentor,
  HomepageComplianceAlert,
  HomepageNews,
} from "@/components/homepage/types";

interface HomepageData {
  news: HomepageNews[];
  articles: HomepageArticle[];
  events: HomepageEvent[];
  courses: HomepageCourse[];
  communities: HomepageCommunity[];
  jobs: HomepageJob[];
  reports: HomepageReport[];
  discussions: HomepageDiscussion[];
  organizations: HomepageOrganization[];
  qaItems: HomepageQA[];
  mentors: HomepageMentor[];
  complianceAlerts: HomepageComplianceAlert[];
  isLoading: boolean;
  error: Error | null;
}

interface ApiResponse<T> {
  data: T;
  total?: number;
  page?: number;
  perPage?: number;
}

function extractData<T>(response: unknown): T[] {
  return ((response as ApiResponse<T>)?.data ?? []) as T[];
}

const QUERY_STALE_TIME = 5 * 60 * 1000;
const QUERY_GC_TIME = 30 * 60 * 1000;

export function useHomepageData(): HomepageData {
  const news = useQuery({
    queryKey: ["homepage", "news"],
    queryFn: () => announcementsApi.listActive({ page: 1, perPage: 10 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const articles = useQuery({
    queryKey: ["homepage", "articles"],
    queryFn: () => blogsApi.list({ page: 1, perPage: 12, sort: "createdAt", order: "desc" }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const events = useQuery({
    queryKey: ["homepage", "events"],
    queryFn: () => eventsApi.list({ page: 1, perPage: 4, status: "upcoming", sort: "date", order: "asc" }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const courses = useQuery({
    queryKey: ["homepage", "courses"],
    queryFn: () => learningApi.listCourses({ page: 1, perPage: 6, sort: "enrolledCount", order: "desc" }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const communities = useQuery({
    queryKey: ["homepage", "communities"],
    queryFn: () => communitiesApi.list({ page: 1, perPage: 4 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const jobs = useQuery({
    queryKey: ["homepage", "jobs"],
    queryFn: () => careerApi.listJobs({ page: 1, perPage: 4 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const reports = useQuery({
    queryKey: ["homepage", "reports"],
    queryFn: () => reportsApi.list({ page: 1, perPage: 8 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const discussions = useQuery({
    queryKey: ["homepage", "discussions"],
    queryFn: () => discussionsApi.list({ page: 1, perPage: 6, sort: "activity", order: "desc" }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const orgs = useQuery({
    queryKey: ["homepage", "organizations"],
    queryFn: () => organizationsApi.list({ page: 1, perPage: 4 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const qa = useQuery({
    queryKey: ["homepage", "qa"],
    queryFn: () => qaApi.list({ page: 1, perPage: 6, sort: "recent", order: "desc" }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const mentors = useQuery({
    queryKey: ["homepage", "mentors"],
    queryFn: () => mentorshipApi.listProfiles(),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const compliance = useQuery({
    queryKey: ["homepage", "compliance"],
    queryFn: () => complianceApi.listAlerts({ page: 1, perPage: 4 }),
    staleTime: QUERY_STALE_TIME,
    gcTime: QUERY_GC_TIME,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const isLoading = news.isLoading || articles.isLoading || events.isLoading ||
    courses.isLoading || communities.isLoading || jobs.isLoading ||
    reports.isLoading || discussions.isLoading || orgs.isLoading ||
    qa.isLoading || mentors.isLoading || compliance.isLoading;

  const error = news.error || articles.error || events.error ||
    courses.error || communities.error || jobs.error ||
    reports.error || discussions.error || orgs.error ||
    qa.error || mentors.error || compliance.error;

  return {
    news: extractData<HomepageNews>(news.data),
    articles: extractData<HomepageArticle>(articles.data),
    events: extractData<HomepageEvent>(events.data),
    courses: extractData<HomepageCourse>(courses.data),
    communities: extractData<HomepageCommunity>(communities.data),
    jobs: extractData<HomepageJob>(jobs.data),
    reports: extractData<HomepageReport>(reports.data),
    discussions: extractData<HomepageDiscussion>(discussions.data),
    organizations: extractData<HomepageOrganization>(orgs.data),
    qaItems: extractData<HomepageQA>(qa.data),
    mentors: extractData<HomepageMentor>(mentors.data),
    complianceAlerts: extractData<HomepageComplianceAlert>(compliance.data),
    isLoading,
    error: error as Error | null,
  };
}
